import React, { useCallback } from "react";
import { AHButton, AHIcon, AHText, Box, TouchableBox, useTheme } from "arkh";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { TeamTab } from "./TeamTab";

import { TeamHomeNavigator } from "./TeamHomeNavigator/TeamHomeNavigator";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import { useNotifyVisitedObjectAtLeaveNav } from "../../OS/VisitedTeam/LastVisitedTeamProvider";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";

import { useSafeAreaInsets } from "react-native-safe-area-context";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";

import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { TeamFiles } from "./TeamFiles";
import { TeamPLUScreen } from "../PLU/PLUScreen";
import { TeamNavigatorQuery } from "./__generated__/TeamNavigatorQuery.graphql";

import { useNavigationHeaderHeight } from "../../TopLevelNavigators/AHNavigationHeaderContainer";
import { TeamHeaderComponent } from "./TeamHomeNavigator/TeamHome/TeamHeader/TeamHeader";
import { TeamNavigatorContext } from "./TeamNavigatorContext";
import { TeamNavigator_team$key } from "./__generated__/TeamNavigator_team.graphql";
import { TeamNavigator_home$key } from "./__generated__/TeamNavigator_home.graphql";
import { TeamNavigator_store$key } from "./__generated__/TeamNavigator_store.graphql";
import { FormattedMessage } from "react-intl";
import { TeamNavigatorNoTeam_store$key } from "./__generated__/TeamNavigatorNoTeam_store.graphql";

import { TeamNavigatorNoTeam_home$key } from "./__generated__/TeamNavigatorNoTeam_home.graphql";
import { TeamNavigatorErrorFallback } from "./TeamNavigatorErrorFallback";
import { usePersistedTeam } from "../../OS/Login/State";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

import { PortalHost } from "@gorhom/portal";
import { CurrentTeamIdProvider } from "widgets/src/CurrentTeamIdContext";
import { TeamNavigator_storeViewer$key } from "./__generated__/TeamNavigator_storeViewer.graphql";
import { PlatformPressable } from "@react-navigation/elements";
import { TeamHeaderProfileButton } from "./TeamHeader/TeamHeaderProfileButton";

export type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"Team">;
  route: UserLoggedInNavigatorRouteProp<"Team">;
};

export const TeamNavigator = (props: Props) => {
  return (
    <LazyQueryNetworkBoundrary
      errorFallback={TeamNavigatorErrorFallback}
      newFetchKeyOnNavigation
    >
      {useCallback(
        (fetchKey: string) => (
          <TeamNavigatorC
            teamId={props.route.params.teamId}
            fetchKey={fetchKey}
            navigation={props.navigation}
          />
        ),
        [props]
      )}
    </LazyQueryNetworkBoundrary>
  );
};

function TeamNavigatorC(props: {
  fetchKey: string;
  teamId?: string;
  navigation: UserLoggedInNavigatorNavigationProp<"Team">;
}) {
  const { currentTeamId } = usePersistedTeam();
  const { currentEmployeeId } = usePersistedEmployeeId();
  const data = useLazyLoadQuery<TeamNavigatorQuery>(
    graphql`
      query TeamNavigatorQuery($id: ID, $employeeId: ID) @throwOnFieldError {
        home {
          ...TeamNavigator_home
          ...TeamNavigatorNoTeam_home
        }
        storeViewer(employeeId: $employeeId) @required(action: THROW) {
          ...TeamNavigator_storeViewer
        }
        store {
          ...TeamNavigator_store
          ...TeamNavigatorNoTeam_store
          publicTeams(includeArchived: false) {
            ...TeamNavigator_team
          }
          team(id: $id) {
            ...TeamNavigator_team
          }
        }
      }
    `,
    {
      id: props.teamId ?? currentTeamId,
      employeeId: currentEmployeeId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  return data.home ? (
    data.store.team ? (
      <InnerTeamNavigator
        {...props}
        team={data.store.team}
        store={data.store}
        home={data.home}
        storeViewer={data.storeViewer}
      />
    ) : (
      <TeamNavigatorNoTeam
        store={data.store}
        home={data.home}
        navigation={props.navigation}
      />
    )
  ) : null;
}

function TeamNavigatorNoTeam(props: {
  home: TeamNavigatorNoTeam_home$key;
  store: TeamNavigatorNoTeam_store$key;
  navigation: UserLoggedInNavigatorNavigationProp<"Team">;
}) {
  const insets = useSafeAreaInsets();
  const store = useFragment(
    graphql`
      fragment TeamNavigatorNoTeam_store on Store {
        myRole
        currentEmployee(id: $employeeId) {
          ...TeamHeaderProfileButton_employee
        }
        me {
          ...TeamHeaderProfileButton_storeUser
        }
      }
    `,
    props.store
  );

  const headerHeight = useNavigationHeaderHeight();
  const home = useFragment(
    graphql`
      fragment TeamNavigatorNoTeam_home on HomeUnion {
        ... on Store {
          __typename
        }
        ... on Organisation {
          __typename
          id
          name
        }
      }
    `,
    props.home
  );

  const intl_tokens = useIntlTokens();

  const hasOrgHeader = home.__typename === "Organisation";

  return (
    <>
      {home ? (
        <Box
          insets={{ top: insets.top }}
          style={{
            height: headerHeight,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {hasOrgHeader ? (
            <TouchableBox
              style={{ flexDirection: "row", alignItems: "center" }}
              onPress={() => {
                props.navigation.goBack();
              }}
              spaceHorizontal="small"
            >
              <AHIcon type="chevron-left" size="medium" />
              <AHText type="title-small" spaceLeft="xsmall">
                {home.name}
              </AHText>
            </TouchableBox>
          ) : null}
        </Box>
      ) : (
        <Box
          style={{
            flexDirection: "row-reverse",
            width: "100%",
            alignItems: "center",
            height: headerHeight,
          }}
          insets={{ top: insets.top }}
        >
          <TeamHeaderProfileButton
            user={store.me ?? null}
            employee={store.currentEmployee ?? null}
            onPress={() => {
              props.navigation.navigate("Profile", {});
            }}
          />
        </Box>
      )}
      <Box
        style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        insets={insets}
      >
        <AHText type="title">
          <FormattedMessage
            defaultMessage="This store does not have any team"
            description="Team navigator no team"
          />
        </AHText>
        {store.myRole === "ADMIN" || store.myRole === "MANAGER" ? (
          <Box spaceTop="xlarge">
            <AHButton
              title={intl_tokens.CreateTeam}
              themeType="primary"
              onPress={() => props.navigation.navigate("TeamCreate", {})}
            />
          </Box>
        ) : null}
      </Box>
    </>
  );
}

function InnerTeamNavigator(props: {
  team: TeamNavigator_team$key;

  home: TeamNavigator_home$key;
  store: TeamNavigator_store$key;
  storeViewer: TeamNavigator_storeViewer$key;
  navigation: UserLoggedInNavigatorNavigationProp<"Team">;
}) {
  const storeViewer = useFragment(
    graphql`
      fragment TeamNavigator_storeViewer on StoreViewer {
        ...TeamHeader_storeViewer
        ...TeamHomeNavigator_storeViewer
      }
    `,
    props.storeViewer
  );
  const store = useFragment(
    graphql`
      fragment TeamNavigator_store on Store {
        id
        myRole
        ...TeamPickerPopover_store
        ...TeamHomeNavigator_store
        ...TeamHeader_store
      }
    `,
    props.store
  );
  const team = useFragment(
    graphql`
      fragment TeamNavigator_team on Team {
        id
        name

        type

        ...TeamHeader_team
      }
    `,
    props.team
  );

  const home = useFragment(
    graphql`
      fragment TeamNavigator_home on HomeUnion {
        ...TeamHeader_home
        ...TeamHomeNavigator_home
        __typename
      }
    `,
    props.home
  );

  useNotifyVisitedObjectAtLeaveNav("team", team.id);

  const intl_tokens = useIntlTokens();
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const hasTabBar = team.type === "CHECKOUT";

  return (
    <CurrentTeamIdProvider value={team.id}>
      <TeamNavigatorContext.Provider
        value={{
          teamId: team.id,
          hasTabBar,
        }}
      >
        <BottomSheetModalProvider>
          <TeamTab.Navigator
            initialRouteName="TeamHome"
            screenOptions={({ route }) => ({
              headerTitle: team.name,
              headerTintColor: "#FFF",
              lazy: true,
              tabBarHideOnKeyboard: true,
              tabBarButton: ({ ref, ...p }) => {
                return (
                  <PlatformPressable
                    {...p}
                    style={{
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  />
                );
              },
              header: () => (
                <TeamHeaderComponent
                  home={home}
                  team={team}
                  store={store}
                  storeViewer={storeViewer}
                  navigation={props.navigation}
                />
              ),
              tabBarStyle: {
                paddingBottom: Math.max(insets.bottom, 5),
                display: hasTabBar ? "flex" : "none",
              },
              tabBarIcon: ({ focused }) => {
                switch (route.name) {
                  case "TeamHome":
                    return (
                      <AHIcon
                        size="medium"
                        type="team-home"
                        color={focused ? "primary" : "washedText"}
                      />
                    );
                  case "TeamFiles":
                    return (
                      <AHIcon
                        size="medium"
                        type="files"
                        color={focused ? "primary" : "washedText"}
                      />
                    );
                  case "TeamPLU":
                    return (
                      <AHIcon
                        size="medium"
                        type="PLU"
                        color={focused ? "primary" : "washedText"}
                      />
                    );
                  default:
                    return null;
                }
              },
              tabBarActiveTintColor: theme.primary,
              tabBarInactiveTintColor: theme.washedText,
            })}
          >
            <TeamTab.Screen
              name="TeamHome"
              initialParams={{
                teamId: team.id,
              }}
              options={{
                headerShown: false,
                title: intl_tokens.Home,
              }}
            >
              {({ navigation }) => {
                return (
                  <TeamHomeNavigator
                    navigation={navigation}
                    home={home}
                    store={store}
                    storeViewer={storeViewer}
                  />
                );
              }}
            </TeamTab.Screen>
            {team.type === "CHECKOUT" ? (
              <TeamTab.Screen
                name="TeamPLU"
                initialParams={{
                  searchText: "",
                  isEditing: false,
                  searching: false,
                  mode: "checkout",
                  teamId: team.id,
                }}
                options={{
                  tabBarHideOnKeyboard: false,
                  title: intl_tokens.PLU,
                }}
                component={TeamPLUScreen}
              />
            ) : null}

            {team.type === "CHECKOUT" ? (
              <TeamTab.Screen
                name="TeamFiles"
                component={TeamFiles}
                initialParams={{
                  teamId: team.id,
                }}
                options={{
                  title: intl_tokens.Files,
                }}
              />
            ) : null}
          </TeamTab.Navigator>
          <PortalHost name="team-popover-portal" />
        </BottomSheetModalProvider>
      </TeamNavigatorContext.Provider>
    </CurrentTeamIdProvider>
  );
}
